// @flow
import React, {useEffect, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';

import {MenuItem, Select, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';

import {colors} from '../../../../assets/styles/colors';
import wsspPurple from '../../../../assets/img/wssp-purple.png';
import CustomButton from '../../../../elements/CustomButton/CustomButton';

import {commaFormat} from '../../../../utils/number';
import {getCustomerFirstName} from '../../utils';
import {generateWhatsappLink, replaceTemplate} from '../../../../utils/share';
import {getPastelColor} from '../../../../utils/style';

import type {
  Store,
  Currency,
  CrmCustomer,
  CustomerCategory,
} from '../../../../entities';

type Props = {
  store?: Store,
  categories?: CustomerCategory[],
  currency?: Currency,
  customer: CrmCustomer,
  isSaving?: boolean,
  message?: string,
  editCustomer: ({[key: string]: any}) => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 48,
    padding: theme.spacing(0, 3),
    width: '100%',
    color: colors.grey700,
    overflow: 'hidden',
  },
  itemColumn: {
    flex: '2 1',
    fontSize: '0.875rem',
  },
  customerName: {
    fontSize: '0.875rem',
  },
  extraColumn: {
    display: 'flex',
    alignItems: 'center',
  },
  seeMoreIcon: {
    color: colors.grey700,
  },
  seeMoreText: {
    color: colors.grey700,
    fontSize: '12px',
    fontWeight: 600,
  },
  selectCategory: {
    width: '90%',
  },
  itemSelect: {
    margin: theme.spacing(0, 1, 1, 1),
    borderRadius: '6px',
    width: '10rem',
  },
  nameCategory: {
    margin: theme.spacing(0.5),
    color: colors.grey800,
    fontSize: '14px',
  },
  wrapIconCustomer: {
    display: 'flex',
    alignItems: 'center',
  },
  iconCustomer: {
    width: '12px',
    height: '12px',
    border: '1px dashed #B8B8C7',
    borderRadius: '100%',
    marginRight: theme.spacing(0.5),
  },
}));
export default function CustomerItem(props: Props) {
  const {
    store,
    currency,
    customer,
    message,
    categories,
    isSaving,
    editCustomer,
  } = props;
  const classes = useStyles();

  const [category, setCategory] = useState('');

  const parsedDate = useMemo(() => {
    if (customer.lastOrderDate) {
      return new Intl.DateTimeFormat('es-PE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }).format(customer.lastOrderDate);
    } else return '--';
  }, [customer.lastOrderDate]);

  const outCategories = useMemo(() => {
    return !categories || (categories && categories.length === 0);
  }, [categories]);

  const customerCategoryColor =
    customer &&
    customer.crmCustomerCategory &&
    customer.crmCustomerCategory.color;

  const handleChange = e => {
    const customerRequest = {
      crmCustomerCategoryId: e.target.value,
    };
    editCustomer({
      customerRequest,
      customerId: customer.id,
    });
  };

  useEffect(() => {
    if (customer.crmCustomerCategory) {
      setCategory(customer.crmCustomerCategory.id);
    } else {
      setCategory('');
    }
  }, [customer]);

  const wsspMessage = useMemo(() => {
    if (!message) return;
    const messageToSend = replaceTemplate(message, {
      nombre_tienda: store ? store.name : '',
      nombre_cliente: getCustomerFirstName(customer),
      link_tienda:
        store && store.domains && store.domains[0].domainName
          ? store.domains[0].domainName
          : '',
    });
    return generateWhatsappLink(customer.phone, messageToSend);
  }, [customer, message, store]);

  return (
    <div className={classes.root}>
      <div className={`${classes.itemColumn} ${classes.wrapIconCustomer}`}>
        <div
          className={classes.iconCustomer}
          style={{
            backgroundColor: customerCategoryColor
              ? customerCategoryColor
              : undefined,
            border: customerCategoryColor
              ? `1px solid ${customerCategoryColor}`
              : undefined,
          }}
        />
        <Typography className={classes.customerName}>
          {customer.fullName}
        </Typography>
      </div>
      <Typography className={classes.itemColumn}>{parsedDate}</Typography>
      <Typography className={classes.itemColumn}>
        {customer.averageOrderAmount
          ? commaFormat(
              customer.averageOrderAmount,
              currency ? currency.symbol : ''
            )
          : '--'}
      </Typography>
      <div className={classes.itemColumn}>
        <Select
          disabled={isSaving || outCategories}
          value={category}
          onChange={handleChange}
          className={classes.selectCategory}
          style={{
            backgroundColor: customerCategoryColor
              ? getPastelColor(customerCategoryColor)
              : undefined,
          }}
        >
          {categories &&
            categories.map((cat, idx) => (
              <MenuItem
                key={`opt-cat-${idx}`}
                value={cat.id}
                className={classes.itemSelect}
                style={{backgroundColor: getPastelColor(cat.color)}}
              >
                <Typography className={classes.nameCategory}>
                  {cat.name}
                </Typography>
              </MenuItem>
            ))}
        </Select>
      </div>
      <Typography className={classes.itemColumn} align="center">
        {customer.numberOfOrders ? customer.numberOfOrders : 0}
      </Typography>
      <div className={classes.extraColumn}>
        <div style={{width: 45}} />
        <CustomButton
          disabled={!message}
          href={wsspMessage}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={wsspPurple} alt="contact wssp" />
        </CustomButton>

        <CustomButton
          component={Link}
          to={`/customers/${customer.id}`}
          endIcon={<ArrowForwardIosIcon className={classes.seeMoreIcon} />}
        >
          <Typography className={classes.seeMoreText}>Ver más</Typography>
        </CustomButton>
      </div>
    </div>
  );
}

CustomerItem.propTypes = {
  store: PropTypes.object,
  categories: PropTypes.array,
  currency: PropTypes.object,
  customer: PropTypes.object.isRequired,
  isSaving: PropTypes.bool,
  message: PropTypes.string,
  editCustomer: PropTypes.func.isRequired,
};
